import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

import type React from 'react';

/**
 * What counts as a field for the purposes of initial focus.
 *
 * Deliberately excludes buttons — see the focus effect below. `type="button"`
 * and friends are buttons wearing an input's tag and are excluded with them,
 * and a disabled or read-only field is not somewhere to put a caret.
 */
const FIELD_SELECTOR = [
  'input:not([type="hidden"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="image"]):not([disabled]):not([readonly])',
  'textarea:not([disabled]):not([readonly])',
  'select:not([disabled])',
  '[contenteditable="true"]',
].join(', ');

const FOCUSABLE_SELECTOR =
  'button, [href], input:not([type="hidden"]), select, textarea, [tabindex]';

/**
 * The controls Tab can actually reach, in order. A selector alone isn't enough:
 * a disabled footer button, a control taken out with `tabindex="-1"`, or one
 * hidden by CSS (`hidden sm:block`) matches it but never takes focus. Were one
 * of those last, focus would never land on "last", the wrap below would never
 * fire, and Tab would walk out of the dialog.
 */
function tabbables(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
  ).filter(
    (el) =>
      el.tabIndex >= 0 &&
      !el.matches(':disabled') &&
      !el.closest('[hidden], [inert]') &&
      // jsdom has no layout, so it has no checkVisibility either.
      (typeof el.checkVisibility !== 'function' || el.checkVisibility())
  );
}

export interface UseDialogFocusOptions {
  /** The dialog is open (or opening). */
  isOpen: boolean;
  /** The dialog's DOM exists: true from mount until its exit animation ends. */
  isRendered: boolean;
  /** The dialog panel, the element with `role="dialog"`. */
  containerRef: React.RefObject<HTMLElement | null>;
  /** Where focus goes on open, overriding the default. */
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  /**
   * Put focus in the first field on open. A centred modal does; a sheet does
   * not, because on a phone a focused field raises the keyboard over a sheet
   * still sliding up — the sheet's close button takes focus instead.
   */
  focusFirstField: boolean;
}

export interface UseDialogFocusReturn {
  /** Keeps Tab inside the dialog. Put it on the dialog panel. */
  onKeyDown: (event: React.KeyboardEvent) => void;
}

/**
 * useDialogFocus — the focus a dialog owes the page, shared by `Modal` and
 * `BottomSheet`: where focus lands on open, Tab kept inside while open, and
 * focus handed back to what opened it once closed.
 *
 * **Fields, not focusables.** The old rule was "first focusable child", and
 * in DOM order that is the ✕ — so a modal whose whole point was a field opened
 * with focus on its close button, anything typed straight away went nowhere,
 * and the first space pressed dismissed the dialog. Buttons are excluded from
 * the field search for exactly that reason, which also means a combobox
 * resolves to its input rather than to the remove-tag buttons before it.
 *
 * With nothing to fill in (or `focusFirstField` off), the ✕ is the target: a
 * real control, and space dismissing a dialog with nothing to do is the
 * behaviour you'd want anyway. The panel itself is the last resort, for a
 * dialog rendered without a close button (mark the ✕ `data-dialog-close`).
 *
 * Focus runs in a layout effect rather than behind a timer: an old 50ms delay
 * landed mid-interaction often enough to look like flake rather than a bug,
 * and nothing needs to wait for an animation — the DOM already exists.
 */
export function useDialogFocus({
  isOpen,
  isRendered,
  containerRef,
  initialFocusRef,
  focusFirstField,
}: UseDialogFocusOptions): UseDialogFocusReturn {
  const previousActiveElement = useRef<HTMLElement | null>(null);
  const wasRendered = useRef(false);

  // What had focus before the dialog, recorded as it opens — before initial
  // focus moves inside.
  useEffect(() => {
    if (isOpen && !wasRendered.current) {
      previousActiveElement.current = document.activeElement as HTMLElement;
    }
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen || !isRendered) return;

    const container = containerRef.current;
    if (!container) return;

    const target =
      initialFocusRef?.current ??
      (focusFirstField
        ? container.querySelector<HTMLElement>(FIELD_SELECTOR)
        : null) ??
      container.querySelector<HTMLElement>('[data-dialog-close]') ??
      container;

    target.focus();
  }, [isOpen, isRendered, containerRef, initialFocusRef, focusFirstField]);

  // Hand focus back once the dialog has gone, unless something took it while
  // the dialog closed: an action here can open what focuses next, such as a
  // form. Focus left inside the dialog falls to the body when it unmounts.
  useEffect(() => {
    if (isRendered) {
      wasRendered.current = true;
      return;
    }
    if (!wasRendered.current) return;
    wasRendered.current = false;

    const active = document.activeElement;
    if (!active || active === document.body) {
      previousActiveElement.current?.focus();
    }
  }, [isRendered]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const container = containerRef.current;
      if (event.key !== 'Tab' || !container) return;

      const focusable = tabbables(container);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    },
    [containerRef]
  );

  return { onKeyDown };
}
